import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { paths } from "../../paths";
import { validate } from "./validate";
import universities from "./universities.json";

// Institutions are almost always searched by acronym + campus (e.g. "IIT
// Bombay", "NIT Warangal", "IIIT Hyderabad") rather than by their full
// official name. Instead of only matching when the ENTIRE input equals a
// bare acronym like "iit", we expand each university's full name into its
// acronym form once (e.g. "Indian Institute of Technology, Bombay" ->
// "iit bombay") so partial, punctuation-insensitive queries match too.
const ACRONYM_MAP = [
  { full: "international institute of information technology", acronym: "iiit" },
  { full: "indian institute of information technology", acronym: "iiit" },
  { full: "indian institute of technology", acronym: "iit" },
  { full: "national institute of technology", acronym: "nit" },
];

// Strip everything but letters/numbers so "Indian Institute of Technology,
// Guwahati" and a query like "iit guwahati" line up despite the comma.
const normalize = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, "");

const buildUniversitySearchIndex = (list) =>
  list.map((university) => {
    const lowerName = university.name.toLowerCase();
    const searchVariants = new Set([normalize(lowerName)]);

    ACRONYM_MAP.forEach(({ full, acronym }) => {
      if (lowerName.includes(full)) {
        searchVariants.add(normalize(lowerName.replace(full, acronym)));
      }
    });

    return { university, searchVariants: Array.from(searchVariants) };
  });

export default function Form() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    college: "",
    contact: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [collegeSuggestions, setCollegeSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const suggestionBoxRef = useRef(null);

  // Built once (universities.json is static), reused on every keystroke.
  const universityIndex = useMemo(
    () => buildUniversitySearchIndex(universities),
    []
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setReferralCode(ref);
      localStorage.setItem("referralCode", ref);
    } else {
      const stored = localStorage.getItem("referralCode");
      if (stored) setReferralCode(stored);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionBoxRef.current &&
        !suggestionBoxRef.current.contains(event.target)
      ) {
        setCollegeSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
  const { name, value } = e.target;
  setFormData({ ...formData, [name]: value });

  if (name === "college") {
    if (value.trim().length > 0) {
      const query = normalize(value);

      // Matches on the full name OR any acronym-expanded variant, so
      // "iit", "iit b", "IIT Bombay", and the full official name all work.
      const filteredData = universityIndex
        .filter(({ searchVariants }) =>
          searchVariants.some((variant) => variant.includes(query))
        )
        .map(({ university }) => university);
      setCollegeSuggestions(filteredData);
    } else {
      setCollegeSuggestions([]);
    }
  }
};

  const handleCollegeSelect = (collegeName) => {
    setFormData({ ...formData, college: collegeName });
    setCollegeSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/signup`, {
        name: formData.name,
        email: formData.email,
        mobile: formData.contact,
        college: formData.college,
        password: formData.password,
        referralCode: referralCode || undefined,
      });
      if (response.status === 200) {
        setLoading(false);
        localStorage.setItem("email", formData.email);
        navigate(paths.otpVerify);
      } else {
        setLoading(false);
        throw new Error("Failed to sign up");
      }
    } catch (error) {
      console.error("Error during form submission:", error);
      setLoading(false);
      // Surface the backend's actual reason ("User already exists" or the
      // specific validation messages) instead of a generic alert.
      const data = error.response?.data;
      const apiMsg =
        Array.isArray(data?.errors) && data.errors.length
          ? data.errors.map((e) => e.msg).join("\n")
          : data?.message;
      alert(apiMsg || "Error registering user");
    }
  };

  return (
    <div className="w-full relative">
      {loading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-50 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Header Context Elements - Adjusted tracking & padding to guarantee 'g' visibility */}
      <h1 className="text-3xl sm:text-7xl font-extrabold font-barlow text-black tracking-tight pb-2 mb-2 block clear-both">
        Sign Up
      </h1>
      <p className="text-[11px] sm:text-xs text-gray-500 font-montserrat leading-relaxed max-w-md mb-6 lg:mb-5">
        Welcome to Spirit, where athletes compete, teams unite, and memories are
        made. More than a sports festival, Spirit is a platform for ambition,
        teamwork, and the pursuit of excellence.
      </p>

      {/* Input controls container */}
      <form
        className="space-y-4 lg:space-y-3 xl:space-y-4"
        onSubmit={handleSubmit}
      >
        {/* Full Name */}
        <div className="flex flex-col gap-1 w-full">
          <label className="block text-[11px] font-bold text-black font-montserrat tracking-wider">
            Full Name
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Emmanuel James"
            required
            className="w-full bg-[#CC5E15]/10 border  text-gray-800 placeholder-black/70 text-xs rounded-sm px-3.5 py-2.5 focus:outline-none focus:ring-1 focus:ring-black/40 transition-all font-montserrat"
          />
          {errors.name && (
            <span className="text-[10px] text-red-600 font-medium">
              {errors.name}
            </span>
          )}
        </div>

        {/* Email Address */}
        <div className="flex flex-col gap-1 w-full">
          <label className="block text-[11px] font-bold text-black font-montserrat tracking-wider">
            Email Address
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="e.g. emmanuelmail@example.com"
            required
            className="w-full bg-[#CC5E15]/10 border  text-gray-800 placeholder-black/70 text-xs rounded-sm px-3.5 py-2.5 focus:outline-none focus:ring-1 focus:ring-black/40 transition-all font-montserrat"
          />
          {errors.email && (
            <span className="text-[10px] text-red-600 font-medium">
              {errors.email}
            </span>
          )}
        </div>

        {/* Contact */}
        <div className="flex flex-col gap-1 w-full">
          <label className="block text-[11px] font-bold text-black font-montserrat tracking-wider">
            Contact
          </label>
          <input
            type="tel"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            placeholder="e.g. 8518498123"
            required
            className="w-full bg-[#CC5E15]/10 border  text-gray-800 placeholder-black/70 text-xs rounded-sm px-3.5 py-2.5 focus:outline-none focus:ring-1 focus:ring-black/40 transition-all font-montserrat"
          />
          {errors.contact && (
            <span className="text-[10px] text-red-600 font-medium">
              {errors.contact}
            </span>
          )}
        </div>

        {/* College Name */}
        <div className="flex flex-col gap-1 w-full relative">
          <label className="block text-[11px] font-bold text-black font-montserrat tracking-wider">
            College Name
          </label>
          <input
            type="text"
            name="college"
            value={formData.college}
            onChange={handleChange}
            placeholder="Indian Institute of Technology, Guwahati"
            required
            className="w-full bg-[#CC5E15]/10 border  text-gray-800 placeholder-black/70 text-xs rounded-sm px-3.5 py-2.5 focus:outline-none focus:ring-1 focus:ring-black/40 transition-all font-montserrat"
          />
          {errors.college && (
            <span className="text-[10px] text-red-600 font-medium">
              {errors.college}
            </span>
          )}

          {collegeSuggestions.length > 0 && (
            <div
              ref={suggestionBoxRef}
              className="absolute left-0 right-0 top-[calc(100%+4px)] bg-[#F4EFE6] border border-black/20 rounded-sm max-h-48 overflow-y-auto z-30 shadow-md"
            >
              {collegeSuggestions.map((college, index) => (
                <div
                  key={index}
                  onClick={() => handleCollegeSelect(college.name)}
                  className="text-xs text-gray-800 px-3.5 py-2 hover:bg-black/5 cursor-pointer transition-colors border-b border-black/5 last:border-0"
                >
                  {college.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Password Row */}
        <div className="flex flex-col sm:flex-row gap-4 lg:gap-3 xl:gap-4 w-full">
          {/* Password */}
          <div className="flex-1 flex flex-col gap-1 min-w-0">
            <label className="block text-[11px] font-bold text-black font-montserrat tracking-wider">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="*********"
                required
                className="w-full bg-[#CC5E15]/10 border  text-gray-800 placeholder-black/70 text-xs rounded-sm px-3.5 py-2.5 pr-10 focus:outline-none focus:ring-1 focus:ring-black/40 transition-all font-mono tracking-widest placeholder:font-sans placeholder:tracking-normal"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-black/70 hover:text-black flex items-center justify-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                </svg>
              </button>
            </div>
            {errors.password && (
              <span className="text-[10px] text-red-600 font-medium">
                {errors.password}
              </span>
            )}
          </div>

          {/* Confirm Password */}
          <div className="flex-1 flex flex-col gap-1 min-w-0">
            <label className="block text-[11px] font-bold text-black font-montserrat tracking-wider">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="*********"
                required
                className="w-full bg-[#CC5E15]/10 border  text-gray-800 placeholder-black/70 text-xs rounded-sm px-3.5 py-2.5 pr-10 focus:outline-none focus:ring-1 focus:ring-black/40 transition-all font-mono tracking-widest placeholder:font-sans placeholder:tracking-normal"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-black/70 hover:text-black flex items-center justify-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                </svg>
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="text-[10px] text-red-600 font-medium">
                {errors.confirmPassword}
              </span>
            )}
          </div>
        </div>

        {/* Informational Subtext */}
        <p className="text-[10px] text-gray-500 font-montserrat leading-normal pt-1">
          Connect with athletes, represent your campus, discover new
          opportunities, and become part of something bigger — together.
        </p>

        {/* CTA Buttons Row */}
        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <button
            type="submit"
            className="flex-1 bg-black hover:bg-gray-900 text-white font-bold text-xs py-3 rounded-sm transition-colors tracking-widest font-montserrat"
          >
            Sign Up
          </button>
        </div>
      </form>

      {/* Clean Bottom Actions Navigation */}
      <div className="flex items-center justify-center gap-1 mt-4 w-full">
        <span className="text-xs text-gray-500 font-montserrat">
          Already a member?
        </span>
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="!text-black text-xs font-bold font-montserrat [text-decoration:underline] hover:text-gray-700 transition-colors bg-transparent p-0 border-none cursor-pointer normal-case !w-auto !min-w-0 !max-w-none"
        >
          Sign In
        </button>
      </div>

      {/* Core Vision Tagline */}
      <div className="text-center mt-4">
        <p className="text-[9px] uppercase tracking-widest text-gray-400 font-medium">
          Together, we're redefining what student sport can be.
        </p>
      </div>
    </div>
  );
}